import { useState, useRef } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import ImageCropper from "@/components/ImageCropper";

/**
 * Reusable image uploader with an in-browser crop step.
 *
 * Props:
 *  - value / onChange(url)
 *  - aspect: 1 for a locked square (logos + headshots), or null for free-form (detail photos)
 *  - label, hint
 *  - square: render the preview as a square (for logos/headshots)
 *  - testid
 */
export default function PhotoUpload({ value, onChange, aspect = null, label, hint, square = false, circle = false, portrait = false, dragPaste = false, testid = "photo-upload" }) {
    const [uploading, setUploading] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [drag, setDrag] = useState(false);
    const pasteRef = useRef(null);
    const mobilePasteRef = useRef(null);
    const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
    const pasteKey = isMac ? "⌘V" : "Ctrl+V";

    const effAspect = circle ? 1 : portrait ? 3 / 4 : aspect;
    const heading = label || (circle ? "Photo (circular)" : effAspect === 1 ? "Logo / headshot (square)" : "Photo (shown full-size on the detail page)");
    const helpText = hint || (circle
        ? "Circular crop · shown as a round avatar."
        : effAspect === 1
            ? "Square crop · appears on cards, the map, and listings."
            : "Free-form crop · displayed in full resolution on the detail page.");

    // Validate + hand the file to the cropper (used by click, drag-drop and paste).
    const acceptFile = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) { toast.error("Please use an image file (JPG/PNG/WebP)"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Image is too large (max 8 MB)"); return; }
        setPendingFile(file);  // open cropper
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";  // allow re-selecting the same file
        acceptFile(file);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        acceptFile(e.dataTransfer?.files?.[0]);
    };

    // Extract an image from a paste/clipboard event.
    const handleClipboard = (e) => {
        const items = e.clipboardData?.items || [];
        for (const it of items) {
            if (it.type && it.type.startsWith("image/")) {
                const file = it.getAsFile();
                if (file) { acceptFile(file); return true; }
            }
        }
        return false;
    };

    const onBoxPaste = (e) => {
        e.preventDefault();
        const el = e.currentTarget;
        if (!handleClipboard(e)) toast.error("No image found in clipboard — copy an image first, then paste");
        setTimeout(() => { try { el.textContent = ""; } catch {} if (pasteRef.current) pasteRef.current.textContent = ""; }, 0);
    };

    // Mobile paste target. It must be a genuinely-editable, FOCUSED element so a
    // long-press shows the OS edit menu ("Paste") — if it isn't focused first,
    // iOS/Safari shows its own page menu ("Open in Reading Mode") instead.
    const onMobilePaste = (e) => {
        e.preventDefault();
        const el = e.currentTarget;
        if (!handleClipboard(e)) toast.error("No image found — copy a photo or screenshot first, then long-press ▸ Paste");
        setTimeout(() => { try { el.textContent = ""; } catch {} }, 0);
    };

    // Mobile-only paste box, sits inline next to Upload. Long-press ▸ Paste.
    const mobilePasteBox = (
        <div className="sm:hidden relative flex-1 min-w-[6.5rem]">
            <div
                ref={mobilePasteRef}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-label="Long-press and choose Paste to add a copied photo or screenshot"
                data-testid={`${testid}-mobile-paste-box`}
                onPaste={onMobilePaste}
                onInput={(e) => { e.currentTarget.textContent = ""; }}
                onKeyDown={(e) => { if (!(e.ctrlKey || e.metaKey)) e.preventDefault(); }}
                onFocus={() => { try { mobilePasteRef.current?.focus(); } catch {} }}
                onTouchStart={() => { try { mobilePasteRef.current?.focus(); } catch {} }}
                style={{ WebkitUserSelect: "text", userSelect: "text" }}
                className="w-full h-9 px-2 flex items-center justify-center border border-dashed border-[#3a3a3a] focus:border-[#ff4500] outline-none text-transparent caret-transparent cursor-text"
            />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[9px] uppercase tracking-wider text-[#a0a0a0] text-center leading-tight px-1">
                Long-press ▸ Paste
            </span>
        </div>
    );

    const onCropComplete = async (blob) => {
        setPendingFile(null);
        setUploading(true);
        try {
            const ext = blob.type === "image/png" ? "png" : "jpg";
            const fd = new FormData();
            fd.append("file", blob, `upload.${ext}`);
            // Let the browser set multipart/form-data WITH its boundary.
            const { data } = await api.post("/uploads", fd);
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const previewClass = circle
        ? "h-16 w-16 object-cover rounded-full border border-[#2a2a2a] bg-white"
        : portrait
        ? "h-24 w-[72px] object-cover border border-[#2a2a2a] bg-white"
        : square
        ? "h-16 w-16 object-cover border border-[#2a2a2a] bg-white"
        : "h-16 w-24 object-cover border border-[#2a2a2a] bg-white";
    const emptyClass = circle
        ? "h-16 w-16 rounded-full border border-dashed border-[#3a3a3a] flex items-center justify-center text-[#666] text-[10px] uppercase tracking-wider"
        : portrait
        ? "h-24 w-[72px] border border-dashed border-[#3a3a3a] flex items-center justify-center text-[#666] text-[10px] uppercase tracking-wider"
        : square
        ? "h-16 w-16 border border-dashed border-[#3a3a3a] flex items-center justify-center text-[#666] text-[10px] uppercase tracking-wider"
        : "h-16 w-24 border border-dashed border-[#3a3a3a] flex items-center justify-center text-[#666] text-[10px] uppercase tracking-wider";

    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{heading}</Label>
            {dragPaste ? (
                <div
                    data-testid={`${testid}-dropzone`}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={onDrop}
                    className={`mt-2 border bg-[#141414] p-3 flex flex-col gap-2 transition-colors ${drag ? "border-[#ff4500] bg-[#ff4500]/5" : "border-[#2a2a2a]"}`}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        {value ? (
                            <img src={value} alt="preview" className={previewClass} />
                        ) : (
                            <div className={emptyClass}>{drag ? "Drop" : "No image"}</div>
                        )}
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-wider transition-colors">
                            <UploadIcon className="h-3.5 w-3.5" />
                            {uploading ? "Uploading…" : (value ? "Replace" : "Upload")}
                            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
                        </label>
                        {mobilePasteBox}
                        {value && (
                            <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-red-500 ml-auto shrink-0">Remove</button>
                        )}
                    </div>
                    <span className="hidden sm:inline text-[10px] text-[#a0a0a0]">Drag &amp; drop, or paste <span className="text-[#cfcfcf] font-semibold">{pasteKey}</span></span>
                    {/* Right-clickable paste target: an editable region so the browser's context menu shows "Paste". Hidden on mobile (no drag/paste there). */}
                    <div className="relative hidden sm:block w-full">
                        <div
                            ref={pasteRef}
                            contentEditable
                            suppressContentEditableWarning
                            role="textbox"
                            aria-label="Paste image here"
                            tabIndex={0}
                            data-testid={`${testid}-paste-box`}
                            onPaste={onBoxPaste}
                            onInput={(e) => { e.currentTarget.textContent = ""; }}
                            onKeyDown={(e) => { if (!(e.ctrlKey || e.metaKey)) e.preventDefault(); }}
                            className="w-full h-9 px-2 flex items-center border border-dashed border-[#3a3a3a] hover:border-[#ff4500] focus:border-[#ff4500] outline-none cursor-text text-[10px] text-transparent caret-transparent"
                        />
                        <span className="pointer-events-none absolute inset-0 flex items-center px-2 text-[10px] uppercase tracking-wider text-[#a0a0a0]">
                            Right-click ▸ Paste (or {pasteKey})
                        </span>
                    </div>
                </div>
            ) : (
                <div className="mt-2 border border-[#2a2a2a] bg-[#141414] p-3 flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                    {value ? (
                        <img src={value} alt="preview" className={previewClass} />
                    ) : (
                        <div className={emptyClass}>No image</div>
                    )}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-wider transition-colors">
                        <UploadIcon className="h-3.5 w-3.5" />
                        {uploading ? "Uploading…" : (value ? "Replace" : "Upload")}
                        <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
                    </label>
                    {mobilePasteBox}
                    {value && (
                        <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-red-500 ml-auto">Remove</button>
                    )}
                    </div>
                </div>
            )}
            <div className="text-[10px] text-[#666] mt-1">{helpText}</div>

            {pendingFile && (
                <ImageCropper
                    file={pendingFile}
                    aspect={effAspect}
                    round={circle}
                    title={heading}
                    onCancel={() => setPendingFile(null)}
                    onComplete={onCropComplete}
                />
            )}
        </div>
    );
}
